import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
    Archive,
    Edit,
    Eye,
    Facebook,
    Globe,
    Megaphone,
    Pause,
    Play,
    Plus,
    Target,
    Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageContainer, PageHeader, StatTile, StatTileGrid } from '@/components/shared/layout';
import { EmptyState } from '@/components/shared/EmptyState';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { GenericDataTable } from '@/components/shared/tables';
import { Column } from '@/components/ui/data-table';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import { useUrlState } from '@/hooks/shared';
import { useConfirm } from '@/hooks/useConfirm';
import { usePermissions } from '@/hooks/usePermissions';
import { ModulePermission, PermissionAction } from '@/types/permissions';
import { useSystemConfiguration } from '@/api/system-configuration/use-system-configuration';
import {
    useCampaigns,
    useDeleteCampaign,
    useUpdateCampaignStatus,
} from '@/api/campaigns/use-campaigns';
import { Campaign, CampaignSource, CampaignStatus } from '@/api/campaigns/types';
import { getCampaignStatusActions } from '@/lib/campaignStatus';

const CampaignsPage: React.FC = () => {
    const { t } = useTranslation('crm');
    const navigate = useNavigate();
    const confirm = useConfirm();
    const { enqueueSnackbar } = useSnackbar();
    const { hasPermission } = usePermissions();
    const { data: systemConfig } = useSystemConfiguration();
    const crmTimezone = systemConfig?.timezone ?? 'UTC';

    const { filters, sort, pagination, updateFilters, clearFilters, toggleSort, goToPage } =
        useUrlState({
            sort: { key: 'created_at', direction: 'desc' },
            pagination: { page: 1, pageSize: 25 },
        });

    const { data: campaignsData, isLoading } = useCampaigns({
        page: pagination.page,
        per_page: pagination.pageSize,
        search: filters.search,
        source: filters.source as CampaignSource | undefined,
        status: filters.status as CampaignStatus | undefined,
        sort_by: sort.key,
        sort_direction: sort.direction,
    });

    const deleteCampaignMutation = useDeleteCampaign();
    const updateStatusMutation = useUpdateCampaignStatus();

    const campaigns = campaignsData?.data || [];
    const totalCampaigns = campaignsData?.meta?.total || 0;
    const totalPages = campaignsData?.meta?.last_page || 1;

    const stats = useMemo(() => {
        const active = campaigns.filter(c => c.status === 'active').length;
        const totalSubmissions = campaigns.reduce(
            (sum, c) => sum + (c.submissions_count?.total ?? 0),
            0
        );
        const leadsCreated = campaigns.reduce(
            (sum, c) => sum + (c.submissions_count?.lead_created ?? 0),
            0
        );
        return {
            total: totalCampaigns,
            active,
            totalSubmissions,
            leadsCreated,
        };
    }, [campaigns, totalCampaigns]);

    const handleChangeStatus = (campaign: Campaign, status: CampaignStatus) => {
        updateStatusMutation.mutate(
            { id: campaign.id, status },
            {
                onSuccess: () => {
                    enqueueSnackbar(t('campaigns.toasts.statusUpdated'), { variant: 'success' });
                },
                onError: () => {
                    enqueueSnackbar(t('campaigns.toasts.statusUpdateFailed'), { variant: 'error' });
                },
            }
        );
    };

    const handleArchive = async (campaign: Campaign) => {
        const ok = await confirm({
            title: t('campaigns.confirm.archiveTitle'),
            description: t('campaigns.confirm.archiveDescription', { name: campaign.name }),
            confirmLabel: t('campaigns.actions.archive'),
        });
        if (!ok) return;
        handleChangeStatus(campaign, 'archived');
    };

    const handleDelete = async (campaign: Campaign) => {
        const ok = await confirm({
            title: t('campaigns.confirm.deleteTitle'),
            description: t('campaigns.confirm.deleteDescription', { name: campaign.name }),
            confirmLabel: t('actions.delete'),
            variant: 'destructive',
        });
        if (!ok) return;

        deleteCampaignMutation.mutate(campaign.id, {
            onSuccess: () => {
                enqueueSnackbar(t('campaigns.toasts.deleted'), { variant: 'success' });
            },
            onError: () => {
                enqueueSnackbar(t('campaigns.toasts.deleteFailed'), { variant: 'error' });
            },
        });
    };

    const sourceBadge = (source: CampaignSource) => (
        <Badge variant="outline" className="gap-1 font-normal">
            {source === 'facebook' ? (
                <Facebook className="h-3 w-3" />
            ) : (
                <Globe className="h-3 w-3" />
            )}
            {t(`campaigns.source.${source}`)}
        </Badge>
    );

    const columns: Column<Campaign>[] = useMemo(
        () => [
            {
                id: 'name',
                label: t('campaigns.columns.name'),
                key: 'name',
                sortable: true,
                width: '220px',
                render: (value, campaign) => (
                    <button
                        type="button"
                        className="font-medium text-foreground hover:text-primary hover:underline"
                        onClick={event => {
                            event.stopPropagation();
                            navigate(`/campaigns/${campaign.id}`);
                        }}
                    >
                        {value}
                    </button>
                ),
            },
            {
                id: 'source',
                label: t('campaigns.columns.source'),
                key: 'source',
                sortable: true,
                width: '160px',
                render: value => sourceBadge(value),
            },
            {
                id: 'status',
                label: t('campaigns.columns.status'),
                key: 'status',
                sortable: true,
                width: '120px',
                render: value => <StatusBadge status={value} kind="campaign" />,
            },
            {
                id: 'submissions',
                label: t('campaigns.columns.submissions'),
                key: 'submissions_count',
                sortable: false,
                width: '220px',
                render: (_, campaign) => {
                    const counts = campaign.submissions_count;
                    return (
                        <span className="text-sm text-muted-foreground">
                            {t('campaigns.submissionsSummary', {
                                total: counts?.total ?? 0,
                                leadCreated: counts?.lead_created ?? 0,
                                failed: counts?.failed ?? 0,
                            })}
                        </span>
                    );
                },
            },
            {
                id: 'created_at',
                label: t('campaigns.columns.created'),
                key: 'created_at',
                sortable: true,
                width: '180px',
                render: value => (
                    <DateTimeFormatter date={value} timeZone={crmTimezone} className="text-sm" />
                ),
            },
            {
                id: 'actions',
                label: '',
                key: 'id',
                sortable: false,
                width: '160px',
                render: (_, campaign) => {
                    const { canActivate, canPause, canArchive } = getCampaignStatusActions(
                        campaign.status
                    );
                    const canEdit = hasPermission(
                        ModulePermission.CAMPAIGNS,
                        PermissionAction.EDIT
                    );
                    const canDelete = hasPermission(
                        ModulePermission.CAMPAIGNS,
                        PermissionAction.DELETE
                    );

                    return (
                        <div className="flex items-center gap-1">
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label={t('campaigns.actions.open')}
                                title={t('campaigns.actions.open')}
                                onClick={event => {
                                    event.stopPropagation();
                                    navigate(`/campaigns/${campaign.id}`);
                                }}
                            >
                                <Eye className="h-4 w-4" />
                            </Button>
                            {canEdit && (
                                <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    aria-label={t('campaigns.actions.edit')}
                                    title={t('campaigns.actions.edit')}
                                    onClick={event => {
                                        event.stopPropagation();
                                        navigate(`/campaigns/${campaign.id}/edit`);
                                    }}
                                >
                                    <Edit className="h-4 w-4" />
                                </Button>
                            )}
                            {canEdit && canActivate && (
                                <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    aria-label={t('campaigns.actions.activate')}
                                    title={t('campaigns.actions.activate')}
                                    onClick={event => {
                                        event.stopPropagation();
                                        handleChangeStatus(campaign, 'active');
                                    }}
                                >
                                    <Play className="h-4 w-4" />
                                </Button>
                            )}
                            {canEdit && canPause && (
                                <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    aria-label={t('campaigns.actions.pause')}
                                    title={t('campaigns.actions.pause')}
                                    onClick={event => {
                                        event.stopPropagation();
                                        handleChangeStatus(campaign, 'paused');
                                    }}
                                >
                                    <Pause className="h-4 w-4" />
                                </Button>
                            )}
                            {canEdit && canArchive && (
                                <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    aria-label={t('campaigns.actions.archive')}
                                    title={t('campaigns.actions.archive')}
                                    onClick={event => {
                                        event.stopPropagation();
                                        handleArchive(campaign);
                                    }}
                                >
                                    <Archive className="h-4 w-4" />
                                </Button>
                            )}
                            {canDelete && (
                                <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    aria-label={t('campaigns.actions.delete')}
                                    title={t('campaigns.actions.delete')}
                                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                                    onClick={event => {
                                        event.stopPropagation();
                                        handleDelete(campaign);
                                    }}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    );
                },
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t, crmTimezone, navigate, hasPermission]
    );

    const filterFields = useMemo(
        () => [
            {
                key: 'search',
                label: t('campaigns.filters.search'),
                type: 'text' as const,
                placeholder: t('campaigns.filters.search'),
            },
            {
                key: 'source',
                label: t('campaigns.filters.source'),
                type: 'select' as const,
                options: [
                    { value: 'facebook', label: t('campaigns.source.facebook') },
                    { value: 'landing_page', label: t('campaigns.source.landing_page') },
                ],
            },
            {
                key: 'status',
                label: t('campaigns.filters.status'),
                type: 'select' as const,
                options: [
                    { value: 'draft', label: 'Draft' },
                    { value: 'active', label: 'Active' },
                    { value: 'paused', label: 'Paused' },
                    { value: 'archived', label: 'Archived' },
                ],
            },
        ],
        [t]
    );

    const handleFilterChange = (newFilters: Record<string, any>) => {
        updateFilters(newFilters);
    };

    const handleSort = (key: keyof Campaign) => {
        toggleSort(key as string);
    };

    const hasActiveFilters = useMemo(
        () => Object.values(filters ?? {}).some(v => v !== '' && v !== null && v !== undefined),
        [filters]
    );

    return (
        <PageContainer fullHeight>
            <PageHeader
                title={t('campaigns.title')}
                description={t('campaigns.description')}
                actions={
                    hasPermission(ModulePermission.CAMPAIGNS, PermissionAction.CREATE) && (
                        <Button onClick={() => navigate('/campaigns/new')}>
                            <Plus className="h-4 w-4" />
                            {t('campaigns.newCampaign')}
                        </Button>
                    )
                }
            />

            <StatTileGrid className="shrink-0">
                <StatTile
                    label={t('campaigns.stats.total')}
                    value={stats.total}
                    icon={Megaphone}
                    tone="brand"
                    isLoading={isLoading}
                />
                <StatTile
                    label={t('campaigns.stats.active')}
                    value={stats.active}
                    icon={Play}
                    tone="success"
                    isLoading={isLoading}
                />
                <StatTile
                    label={t('campaigns.stats.submissions')}
                    value={stats.totalSubmissions}
                    icon={Target}
                    tone="info"
                    isLoading={isLoading}
                />
                <StatTile
                    label={t('campaigns.stats.leadsCreated')}
                    value={stats.leadsCreated}
                    icon={Target}
                    tone="success"
                    isLoading={isLoading}
                />
            </StatTileGrid>

            <GenericDataTable<Campaign>
                data={campaigns}
                columns={columns}
                visibleColumns={columns.map(col => col.id)}
                filters={filters}
                filterFields={filterFields}
                presets={[]}
                isLoading={isLoading}
                selectedItems={[]}
                onSelectionChange={() => {}}
                getItemId={campaign => campaign.id}
                sortConfig={{ key: sort.key as keyof Campaign, direction: sort.direction }}
                onSort={handleSort}
                totalItems={totalCampaigns}
                totalPages={totalPages}
                currentPage={pagination.page}
                pageSize={pagination.pageSize}
                onPageChange={goToPage}
                showExportButton={false}
                onRowClick={campaign => navigate(`/campaigns/${campaign.id}`)}
                onFilterChange={handleFilterChange}
                onClearFilters={clearFilters}
                onColumnToggle={() => {}}
                onResetColumns={() => {}}
                emptyMessage={t('campaigns.empty.title')}
                loadingMessage={t('table.loading')}
                stickyLayout
                emptyState={
                    <EmptyState
                        icon={Megaphone}
                        title={t('campaigns.empty.title')}
                        description={
                            hasActiveFilters
                                ? t('campaigns.empty.noMatches')
                                : t('campaigns.empty.nothingYet')
                        }
                        action={
                            hasActiveFilters ? (
                                <Button variant="outline" size="sm" onClick={clearFilters}>
                                    {t('actions.clearFilters')}
                                </Button>
                            ) : hasPermission(
                                  ModulePermission.CAMPAIGNS,
                                  PermissionAction.CREATE
                              ) ? (
                                <Button size="sm" onClick={() => navigate('/campaigns/new')}>
                                    <Plus className="h-4 w-4" />
                                    {t('campaigns.newCampaign')}
                                </Button>
                            ) : undefined
                        }
                    />
                }
            />
        </PageContainer>
    );
};

export default CampaignsPage;
